<template>
  <!--
    License picker. Restructured to be vanish-proof:
    - Every license radio is a flat sibling - no nested layouts that
      could collapse, no flex-wrap shenanigans.
    - The custom-template text input is rendered only while "Enter a
      different license" is the active choice (v-if on isCustomActive),
      per issue #17. Because it never auto-focuses, mounting it does not
      pop the mobile keyboard, so it stays vanish-proof without needing
      to reserve permanent vertical space.
    - No auto-focus. The user already clicked the radio; auto-focusing
      a text input on mobile pops up the on-screen keyboard, which
      covers half the screen and makes the user think the form
      "vanished". They can tap the input themselves when ready.
    - No funky @click handlers - plain @change on radios, plain @input
      on the text field.
  -->
  <div class="c2c-licp" role="radiogroup" :aria-label="ariaLabel || t('provenance.own_q2')">
    <label
      v-for="lic in allowed"
      :key="lic"
      class="c2c-licp-row"
      :class="{ 'c2c-licp-row--active': isPresetActive(lic) }"
    >
      <input
        type="radio"
        :name="groupName"
        :checked="isPresetActive(lic)"
        @change="setPreset(lic)"
      />
      <span class="c2c-licp-text">
        <span class="c2c-licp-title">{{ presetTitle(lic) }}</span>
        <small class="c2c-licp-blurb">{{ presetBlurb(lic) }}</small>
      </span>
    </label>

    <label
      v-if="allowCustom"
      class="c2c-licp-row"
      :class="{ 'c2c-licp-row--active': isCustomActive }"
    >
      <input
        type="radio"
        :name="groupName"
        :checked="isCustomActive"
        @change="setCustom"
      />
      <span class="c2c-licp-text">
        <span class="c2c-licp-title">{{ t('provenance.license_enter_different') }}</span>
      </span>
    </label>

    <label
      v-if="allowUnknown"
      class="c2c-licp-row"
      :class="{ 'c2c-licp-row--active': isUnknownActive }"
    >
      <input
        type="radio"
        :name="groupName"
        :checked="isUnknownActive"
        @change="setUnknown"
      />
      <span class="c2c-licp-text">
        <span class="c2c-licp-title">{{ t('provenance.license_dont_know') }}</span>
      </span>
    </label>

    <!--
      Custom-template text field. Shown only while "Enter a different
      license" is active (v-if on isCustomActive). It does not auto-focus,
      so mounting it on radio-select never pops the mobile keyboard.
    -->
    <div
      v-if="allowCustom && isCustomActive"
      class="c2c-licp-custom"
      :class="{ 'c2c-licp-custom--active': isCustomActive }"
    >
      <label class="c2c-licp-custom-label" :for="customInputId">
        {{ t('provenance.license_enter_help') }}
      </label>
      <!--
        Typing a template name from memory is how people get "cc-by-2.0-DE"
        or "PD-USGov-NIH" wrong and land a file in a maintenance category.
        The list is the real spelling of 75 templates that were checked
        against the live Commons API, so picking beats typing.
      -->
      <div class="c2c-licp-combo">
        <input
          :id="customInputId"
          ref="customInputEl"
          type="text"
          class="c2c-licp-custom-input"
          role="combobox"
          :placeholder="t('provenance.license_enter_placeholder')"
          :value="customTextDisplay"
          :disabled="!isCustomActive"
          :aria-expanded="suggestOpen && matches.length > 0"
          aria-autocomplete="list"
          :aria-controls="listId"
          :aria-activedescendant="activeIndex >= 0 ? optionId(activeIndex) : undefined"
          @input="onCustomInput"
          @focus="onCustomFocus"
          @blur="suggestOpen = false"
          @keydown.down.prevent="moveSuggestion(1)"
          @keydown.up.prevent="moveSuggestion(-1)"
          @keydown.enter="onSuggestionEnter"
          @keydown.escape="suggestOpen = false"
          autocomplete="off"
          spellcheck="false"
        />

        <ul
          v-if="suggestOpen && matches.length > 0"
          :id="listId"
          class="c2c-licp-suggestions"
          role="listbox"
        >
          <li
            v-for="(m, i) in matches"
            :key="m.name"
            :id="optionId(i)"
            class="c2c-licp-suggestion"
            :class="{ 'c2c-licp-suggestion--active': i === activeIndex }"
            role="option"
            :aria-selected="i === activeIndex"
            @mousedown.prevent="chooseTemplate(m.name)"
            @mousemove="activeIndex = i"
          >
            <strong>{{ m.name }}</strong>
            <small>{{ m.hint }}</small>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { searchLicenseTemplates } from "@/lib/license-templates"
import { useI18n } from "vue-i18n"
import type { LicenseChoice, LicenseId } from "@/lib/types"

const { t } = useI18n()

const props = defineProps<{
  modelValue: LicenseChoice
  allowed: LicenseId[]
  allowCustom: boolean
  allowUnknown: boolean
  ariaLabel?: string
}>()
const emit = defineEmits<{ "update:modelValue": [LicenseChoice] }>()

const uid = Math.random().toString(36).slice(2, 8)
const groupName = "lic-" + uid
const customInputId = "lic-custom-" + uid

/**
 * Cached custom-template text. Persists across radio-toggles inside
 * this picker so a brief tap on a CC preset doesn't lose the user's
 * typed string.
 */
const lastCustomTemplate = ref("")
watch(
  () => props.modelValue,
  (m) => { if (m.kind === "custom") lastCustomTemplate.value = m.template },
  { immediate: true }
)

function isPresetActive(id: LicenseId): boolean {
  return props.modelValue.kind === "preset" && props.modelValue.id === id
}
const isCustomActive = computed(() => props.modelValue.kind === "custom")
const isUnknownActive = computed(() => props.modelValue.kind === "unknown")

const customTextDisplay = computed(() =>
  props.modelValue.kind === "custom" ? props.modelValue.template : lastCustomTemplate.value
)

function setPreset(id: LicenseId) {
  emit("update:modelValue", { kind: "preset", id })
}
function setCustom() {
  emit("update:modelValue", { kind: "custom", template: lastCustomTemplate.value })
}
function ensureCustomActive() {
  // If user taps the disabled-looking input, switch to custom mode.
  if (!isCustomActive.value) setCustom()
}
function onCustomInput(e: Event) {
  const v = (e.target as HTMLInputElement).value
  lastCustomTemplate.value = v
  suggestOpen.value = true
  activeIndex.value = -1
  emit("update:modelValue", { kind: "custom", template: v })
}

/* ---- template suggestions ---- */

const customInputEl = ref<HTMLInputElement | null>(null)
const suggestOpen = ref(false)
const activeIndex = ref(-1)
const listId = "lic-tpl-" + uid
const optionId = (i: number) => "lic-tpl-" + uid + "-" + i

const matches = computed(() =>
  isCustomActive.value ? searchLicenseTemplates(customTextDisplay.value) : [])

function onCustomFocus() {
  ensureCustomActive()
  suggestOpen.value = true
  activeIndex.value = -1
}

function moveSuggestion(delta: number) {
  const n = matches.value.length
  if (!n) return
  suggestOpen.value = true
  const next = activeIndex.value + delta
  activeIndex.value = next < 0 ? n - 1 : next >= n ? 0 : next
}

/** Enter picks the highlighted row, and otherwise falls through to the form
 *  so the key keeps doing what it did before this list existed. */
function onSuggestionEnter(e: KeyboardEvent) {
  if (!suggestOpen.value || activeIndex.value < 0) return
  e.preventDefault()
  chooseTemplate(matches.value[activeIndex.value].name)
}

function chooseTemplate(name: string) {
  lastCustomTemplate.value = name
  suggestOpen.value = false
  activeIndex.value = -1
  emit("update:modelValue", { kind: "custom", template: name })
}
function setUnknown() {
  emit("update:modelValue", { kind: "unknown" })
}

/* ---- preset metadata ---- */

function presetTitle(id: LicenseId): string {
  switch (id) {
    case "cc-zero": return "CC0 (public domain dedication)"
    case "cc-by-4.0": return "CC BY 4.0"
    case "cc-by-3.0": return "CC BY 3.0"
    case "cc-by-2.5": return "CC BY 2.5"
    case "cc-by-2.0": return "CC BY 2.0"
    case "cc-by-sa-4.0": return "CC BY-SA 4.0"
    case "cc-by-sa-3.0": return "CC BY-SA 3.0"
    case "cc-by-sa-2.5": return "CC BY-SA 2.5"
    case "cc-by-sa-2.0": return "CC BY-SA 2.0"
    case "gfdl": return "GFDL"
    case "fal": return "Free Art License"
    case "pd-self": return "Public Domain (own work)"
  }
}
function presetBlurb(id: LicenseId): string {
  if (id === "cc-zero") return t('provenance.license_blurb_cc0')
  if (id.startsWith("cc-by-sa")) return t('provenance.license_blurb_by_sa')
  if (id.startsWith("cc-by")) return t('provenance.license_blurb_by')
  return ""
}
</script>

<style scoped>
.c2c-licp { display: flex; flex-direction: column; gap: 4px; }

.c2c-licp-row {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px; line-height: 1.4;
  background: transparent;
}
.c2c-licp-row:hover { background: #f8f9fa; }
.c2c-licp-row--active {
  background: #eaf3ff;
  border-color: #c8d6f0;
}
.c2c-licp-row input[type="radio"] {
  flex-shrink: 0;
  width: 18px; height: 18px;
  margin-top: 1px;
  accent-color: #3366cc;
  cursor: pointer;
}
.c2c-licp-text {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 2px;
}
.c2c-licp-title { font-weight: 500; color: #202122; }
.c2c-licp-blurb { font-size: 12px; color: #54595d; line-height: 1.4; }

.c2c-licp-custom {
  margin-top: 8px;
  padding: 12px;
  border: 1px dashed #c8ccd1;
  border-radius: 6px;
  background: #f8f9fa;
  display: flex; flex-direction: column; gap: 6px;
  transition: border-color 0.15s, background 0.15s, opacity 0.15s;
  opacity: 0.6;
}
/* The row above is already tinted and ringed to show it is the live choice;
   a second saturated blue border around the box nested inside it read as a
   double border. A hairline in the same family is enough. */
.c2c-licp-custom--active {
  border-style: solid;
  border-color: #c8d6f0;
  background: #eaf3ff;
  opacity: 1;
}
.c2c-licp-custom-label { font-size: 12px; color: #54595d; }
.c2c-licp-custom--active .c2c-licp-custom-label { color: #202122; font-weight: 500; }
.c2c-licp-custom-input {
  padding: 10px 12px; border: 1px solid #a2a9b1; border-radius: 4px;
  font: inherit; font-size: 16px; box-sizing: border-box; width: 100%;
  min-height: 44px; background: #fff;
}
.c2c-licp-custom-input:focus {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}

.c2c-licp-combo { position: relative; }
.c2c-licp-suggestions {
  position: absolute; z-index: 30; top: calc(100% + 4px); inset-inline: 0;
  margin: 0; padding: 4px; list-style: none;
  max-height: 260px; overflow-y: auto;
  background: #fff;
  border: 1px solid #c8ccd1; border-radius: 10px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14), 0 2px 6px rgba(0, 0, 0, 0.08);
}
.c2c-licp-suggestion {
  display: flex; flex-direction: column; gap: 1px;
  padding: 8px 10px; border-radius: 6px; cursor: pointer;
}
.c2c-licp-suggestion--active { background: #eaf3ff; }
.c2c-licp-suggestion strong {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px; font-weight: 600; color: #202122;
}
.c2c-licp-suggestion small { font-size: 12px; color: #54595d; }
.c2c-licp-custom-input:disabled {
  background: #f0f0f0; color: #72777d; cursor: pointer;
}
</style>